"use client"

import type React from "react"
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react"
import type { VideoHTMLAttributes } from "react"

export interface VideoSource {
  uri?: string
  [key: string]: any
}

export interface VideoProps extends Omit<VideoHTMLAttributes<HTMLVideoElement>, "onLoad" | "onError" | "onProgress"> {
  source?: VideoSource | string
  paused?: boolean
  muted?: boolean
  volume?: number
  rate?: number
  resizeMode?: "contain" | "cover" | "stretch" | "center"
  repeat?: boolean

  fullscreen?: boolean
  testID?: string
  onLoad?: (data: any) => void
  onProgress?: (data: any) => void
  onEnd?: () => void
  onBack?: () => void
  onError?: (error: { error: { code?: number; domain?: string } }) => void
}

export interface VideoRef {
  seek: (time: number) => void
  presentFullscreenPlayer: () => void
  dismissFullscreenPlayer: () => void
}

function normalizeDomStyle(s: any, extra?: React.CSSProperties): React.CSSProperties {
  let base: React.CSSProperties = {}
  if (Array.isArray(s)) base = Object.assign({}, ...s.filter(Boolean))
  else if (s && typeof s === "object") base = s as React.CSSProperties
  return { ...base, ...(extra || {}) }
}

const isIOS = typeof navigator !== "undefined" && /iPhone|iPad|iPod/i.test(navigator.userAgent)

const Video = forwardRef<VideoRef, VideoProps>((props, ref) => {
  const {
    source,
    paused,
    muted,
    controls = true,
    repeat,
    resizeMode = "cover",
    style,
    onLoad,
    onProgress,
    onEnd,
    onBack,
    onError,
    testID,
    ...restProps
  } = props

  const videoRef = useRef<HTMLVideoElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // flags
  const hasEnteredFullscreen = useRef(false)
  const hasCalledExitCallback = useRef(false)

  const resetFlags = () => {
    hasEnteredFullscreen.current = false
    hasCalledExitCallback.current = false
  }

  const getVideoSource = (): string => {
    if (typeof source === "string") return source
    if (source?.uri) return source.uri
    return ""
  }

  const getObjectFit = (): React.CSSProperties["objectFit"] => {
    switch (resizeMode) {
      case "contain":
        return "contain"
      case "cover":
        return "cover"
      case "stretch":
        return "fill"
      case "center":
        return "none"
      default:
        return "cover"
    }
  }

  const isIOSFullscreen = () => {
    const v = videoRef.current as any
    return !!(v && v.webkitDisplayingFullscreen)
  }

  const callExit = (naturalEnd: boolean) => {
    if (hasCalledExitCallback.current) return
    hasCalledExitCallback.current = true
    if (naturalEnd) {
      onEnd?.()
    } else {
      if (onBack) onBack()
      else onEnd?.()
    }
  }

  useImperativeHandle(ref, () => ({
    seek: (time: number) => {
      if (videoRef.current) videoRef.current.currentTime = time
    },
    presentFullscreenPlayer: () => {
      const v = videoRef.current as any
      if (isIOS && v?.webkitEnterFullscreen) {
        v.webkitEnterFullscreen()
        hasEnteredFullscreen.current = true
      } else if (containerRef.current?.requestFullscreen) {
        containerRef.current.requestFullscreen()
        hasEnteredFullscreen.current = true
      }
    },
    dismissFullscreenPlayer: () => {
      const v = videoRef.current as any
      if (isIOS && v?.webkitExitFullscreen) {
        v.webkitExitFullscreen()
      } else if (document.exitFullscreen) {
        document.exitFullscreen()
      }
    },
  }))

  // Entrada automática em FS (se você quiser manter)
  useEffect(() => {
    const video = videoRef.current as any
    const container = containerRef.current
    if (!video || !container || hasEnteredFullscreen.current) return

    const enterFullscreen = async () => {
      try {
        if (isIOS && typeof video.webkitEnterFullscreen === "function") {
          video.webkitEnterFullscreen()
          hasEnteredFullscreen.current = true
        } else if (container.requestFullscreen && !document.fullscreenElement) {
          await container.requestFullscreen()
          hasEnteredFullscreen.current = true
        }
      } catch (err) {
        // silencioso no iOS quando não é gesto de usuário
      }
    }

    const t = setTimeout(enterFullscreen, 100)
    return () => clearTimeout(t)
  }, [])

  // Listeners (iOS + desktop)
  useEffect(() => {
    const v = videoRef.current
    if (!v) return

    const onLoadedData = () => {
      if (videoRef.current && onLoad) {
        onLoad({
          currentTime: videoRef.current.currentTime,
          duration: videoRef.current.duration,
          naturalSize: {
            width: videoRef.current.videoWidth,
            height: videoRef.current.videoHeight,
            orientation: videoRef.current.videoWidth > videoRef.current.videoHeight ? "landscape" : "portrait",
          },
        })
      }
    }

    const onTimeUpdate = () => {
      if (videoRef.current && onProgress) {
        onProgress({
          currentTime: videoRef.current.currentTime,
          seekableDuration: videoRef.current.duration,
        })
      }
    }

    const onEnded = () => {
      // fim natural
      callExit(true)
    }

    const onErrorHandler = () => {
      onError?.({ error: { code: 1, domain: "VideoError" } })
    }

    // iOS: começo e fim do fullscreen nativo
    const handleIOSBeginFS = () => {
      hasEnteredFullscreen.current = true
      hasCalledExitCallback.current = false
    }

    const handleIOSEndFS = () => {
      // Saiu do FS. Ver se terminou ou apenas abandonou.
      const el = videoRef.current
      if (!el) return
      const naturalEnd =
        el.ended ||
        (Number.isFinite(el.duration) &&
          el.duration > 0 &&
          Math.abs(el.currentTime - el.duration) < 0.3)
      callExit(naturalEnd)
    }

    // Desktop/Android: change do Fullscreen API
    const handleDocFsChange = () => {
      if (!document.fullscreenElement && hasEnteredFullscreen.current) {
        const el = videoRef.current
        if (!el) return
        const naturalEnd =
          el.ended ||
          (Number.isFinite(el.duration) &&
            el.duration > 0 &&
            Math.abs(el.currentTime - el.duration) < 0.3)
        callExit(naturalEnd)
      }
    }

    // Fallback: pause quando já tinha entrado em FS e não está mais em FS (iOS)
    const handlePause = () => {
      if (!hasEnteredFullscreen.current) return
      if (isIOS && !isIOSFullscreen() && !hasCalledExitCallback.current) {
        const el = videoRef.current!
        const naturalEnd =
          el.ended ||
          (Number.isFinite(el.duration) &&
            el.duration > 0 &&
            Math.abs(el.currentTime - el.duration) < 0.3)
        callExit(naturalEnd)
      }
    }

    // Fallback adicional: quando a página volta a ficar visível após o player nativo
    const handleVisibility = () => {
      if (!document.hidden && hasEnteredFullscreen.current && !isIOSFullscreen()) {
        const el = videoRef.current!
        const naturalEnd =
          el.ended ||
          (Number.isFinite(el.duration) &&
            el.duration > 0 &&
            Math.abs(el.currentTime - el.duration) < 0.3)
        callExit(naturalEnd)
      }
    }

    // Registrar tudo
    v.addEventListener("loadeddata", onLoadedData)
    v.addEventListener("timeupdate", onTimeUpdate)
    v.addEventListener("ended", onEnded)
    v.addEventListener("error", onErrorHandler)
    v.addEventListener("pause", handlePause)

    // ⚠️ Sem feature-detect aqui — iOS ainda dispara esses eventos mesmo que as props não existam
    v.addEventListener("webkitbeginfullscreen" as any, handleIOSBeginFS as any)
    v.addEventListener("webkitendfullscreen" as any, handleIOSEndFS as any)

    document.addEventListener("fullscreenchange", handleDocFsChange)
    document.addEventListener("visibilitychange", handleVisibility)

    return () => {
      v.removeEventListener("loadeddata", onLoadedData)
      v.removeEventListener("timeupdate", onTimeUpdate)
      v.removeEventListener("ended", onEnded)
      v.removeEventListener("error", onErrorHandler)
      v.removeEventListener("pause", handlePause)

      v.removeEventListener("webkitbeginfullscreen" as any, handleIOSBeginFS as any)
      v.removeEventListener("webkitendfullscreen" as any, handleIOSEndFS as any)

      document.removeEventListener("fullscreenchange", handleDocFsChange)
      document.removeEventListener("visibilitychange", handleVisibility)

      resetFlags()
    }
  }, [onEnd, onBack, onError, onLoad, onProgress])

  const src = getVideoSource()
  const objectFit = getObjectFit()

  return (
    <div ref={containerRef} style={fullscreenWrapperStyle}>
      <video
        ref={videoRef}
        src={src}
        controls={controls}
        muted={muted}
        loop={repeat}
        autoPlay={!paused}
        playsInline
        style={normalizeDomStyle(style, { ...fullscreenVideoStyle, objectFit })}
        data-testid={testID}
        {...restProps}
      />
    </div>
  )
})

Video.displayName = "Video"
export default Video

const fullscreenWrapperStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  width: "100vw",
  height: "100vh",
  backgroundColor: "black",
  zIndex: 9999,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
}

const fullscreenVideoStyle: React.CSSProperties = {
  width: "100vw",
  height: "100vh",
  outline: "none",
  backgroundColor: "black",
  display: "block",
}
